import type { Commit } from "@stepwisehq/prosemirror-collab-commit/collab-commit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import type { PresenceIndicator, StepMapEntry } from "./presence.types";

/**
 * Stores presence indicators per client: { [clientId]: PresenceIndicator[] }
 * Each client can have multiple indicators at different versions.
 */
export const $presenceIndicators = atomFamily((key: string) => {
	const indicatorsAtom = atom({} as Record<string, PresenceIndicator[]>);
	indicatorsAtom.debugLabel = `presenceIndicators::${key}`;
	return indicatorsAtom;
});

/**
 * Stores step maps for rebasing indicators across document versions.
 */
export const $stepMaps = atomFamily((key: string) => {
	const stepMapsAtom = atom([] as StepMapEntry[]);
	stepMapsAtom.debugLabel = `stepMaps::${key}`;
	return stepMapsAtom;
});

/**
 * Updates presence indicators when received from server.
 *
 * Faithfully, lightly adapted from page.machine.ts lines 2086-2119, which corresponds to the
 * `page.presenceIndicatorsReceived` event handler.
 */
export const $onPresenceIndicatorsReceived = atom(
	null,
	(
		get,
		set,
		input: {
			key: string;
			localClientId: string;
			indicators: Record<string, PresenceIndicator>;
		}
	) => {
		const { key, localClientId, indicators: received } = input;
		const presenceIndicators = get($presenceIndicators(key));

		const updatedPresenceIndicators: Record<string, PresenceIndicator[]> = {};
		for (const [clientId, indicator] of Object.entries(received)) {
			// Skip own client.
			if (clientId === localClientId) {
				continue;
			}

			if (clientId in presenceIndicators) {
				// Merge: keep different versions, replace same version
				updatedPresenceIndicators[clientId] = [
					// We may get a new indicator for an existing version, because the version is
					// only updated when the actual document contents change, but a user can move
					// their cursor without updating the document.
					...presenceIndicators[clientId].filter((i) => i.version !== indicator.version),
					indicator,
				];
			} else {
				updatedPresenceIndicators[clientId] = [indicator];
			}
		}

		//
		// NOTE: At this point we've implicitly dropped client IDs for anyone who has left the
		// document. They wouldn't have been returned in the `received` object, and would therefore
		// have been dropped.
		//

		set($presenceIndicators(key), updatedPresenceIndicators);
	}
);
$onPresenceIndicatorsReceived.debugLabel = "receivePresenceIndicators";

/**
 * Updates step maps and cleans stale presence indicators when remote commits are received.
 *
 * Faithfully, lightly adapted from page.machine.ts lines 1385-1442, which corresponds to the
 * `receiveRemoteTransaction` action handler.
 */
export const $onRemoteCommitsReceived = atom(
	null,
	(
		get,
		set,
		input: {
			key: string;
			newVersion: number; // generally from getVersion(newEditorState) after applying commits
			commits: Commit[];
		}
	) => {
		const { key, newVersion, commits } = input;

		//
		// Remove any "stale" indicators. We only care about:
		//
		//   1. Indicators with "future" versions
		//   2. The *most recent* indicator with a present or past version
		//
		// Any indicators other than the most recent with a present or past version can be dropped,
		// since we will always use the most recent.
		//

		const currentIndicators = get($presenceIndicators(key));
		const cleanedIndicators = Object.fromEntries(
			Object.entries(currentIndicators).map(([clientId, indicators]) => [
				clientId,
				indicators.reduce<PresenceIndicator[]>((acc, indicator) => {
					const prev = acc.at(-1);
					if (!prev) {
						return [indicator];
					}

					// Keep future indicators.
					if (prev.version > newVersion) {
						return [...acc, indicator];
					}
					if (indicator.version > newVersion) {
						return [...acc, indicator];
					}

					// Otherwise, only keep most recent past/present indicator.
					return [indicator];
				}, []),
			])
		);

		const earliestIndicatorVersion = Array.from(Object.values(currentIndicators))
			.flat()
			.reduce((acc, indicator) => Math.min(acc, indicator.version), newVersion);

		// Extract step maps from commits.
		const newStepMaps: StepMapEntry[] = commits.map((commit) => ({
			version: commit.version,
			mappables: commit.steps.map((step) => step.getMap()),
		}));

		// We can drop any stepMaps for versions earlier than the earliest presence indicator, since
		// we only use them for "catching up" presence indicators.
		const currentStepMaps = get($stepMaps(key));

		// Deduplicate: only add step maps for versions we don't already have.
		const existingVersions = new Set(currentStepMaps.map((sm) => sm.version));
		const deduplicatedNewStepMaps = newStepMaps.filter(
			(sm) => !existingVersions.has(sm.version)
		);

		const updatedStepMaps = [...currentStepMaps, ...deduplicatedNewStepMaps].filter(
			(sm) => sm.version >= earliestIndicatorVersion
		);

		set($presenceIndicators(key), cleanedIndicators);
		set($stepMaps(key), updatedStepMaps);
	}
);
$onRemoteCommitsReceived.debugLabel = "onRemoteCommitsReceived";

/**
 * Cleans up all presence state for a key.
 */
export const $cleanupPresence = atom(null, (_get, set, key: string) => {
	set($presenceIndicators(key), {});
	set($stepMaps(key), []);
	$presenceIndicators.remove(key);
	$stepMaps.remove(key);
});
$cleanupPresence.debugLabel = "cleanupPresence";

/**
 * A present user derived from presence indicators.
 */
export type PresentUser = {
	/** The user's ID (from auth) */
	userID: string;
	/** The client/tab ID (unique per browser tab) */
	tabID: string;
};

/**
 * Derived atom that provides the list of present users for a given key.
 *
 * Transforms presence indicators into a sorted list of unique users. Each user appears once, even
 * if they have multiple presence indicators (e.g., from cursor movements). Users are sorted by
 * userID for stable ordering.
 */
export const $presentUsers = atomFamily((key: string) => {
	const usersAtom = atom((get) => {
		const indicators = get($presenceIndicators(key));

		// Extract unique users from presence indicators.
		// Use a Map keyed by userID to dedupe users with multiple tabs/clients.
		const userMap = new Map<string, PresentUser>();

		for (const [clientId, indicatorList] of Object.entries(indicators)) {
			const latestIndicator = indicatorList[0];
			if (latestIndicator?.userId) {
				// Only add if we haven't seen this user yet (first client wins)
				if (!userMap.has(latestIndicator.userId)) {
					userMap.set(latestIndicator.userId, {
						userID: latestIndicator.userId,
						tabID: clientId,
					});
				}
			}
		}

		//
		// NOTE: At this point we've implicitly dropped client IDs for anyone who has left the
		// document. They wouldn't have been returned in the `indicators` object, and would
		// therefore have been dropped.
		//

		// Sort by userID for stable ordering
		return Array.from(userMap.values()).sort((a, b) => a.userID.localeCompare(b.userID));
	});
	usersAtom.debugLabel = `presentUsers::${key}`;
	return usersAtom;
});
